import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  getReportedListings, 
  handleListingReport, 
  bulkHandleListingReports
} from '../../services/api';
import {
  FiAlertTriangle,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiShield,
  FiTrash2,
  FiFilter,
  FiSearch,
  FiClock,
  FiUser,
  FiChevronLeft,
  FiChevronRight,
  FiMoreVertical,
  FiDownload,
  FiBarChart2,
  FiRefreshCw,
  FiPackage,
  FiX,
  FiAlertCircle,
  FiGrid,
  FiList
} from 'react-icons/fi';

function ReportedListings({ admin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReports, setSelectedReports] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [stats, setStats] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('table');
  const [actionModalData, setActionModalData] = useState(null);
  
  const [filters, setFilters] = useState({
    status: queryParams.get('status') || 'pending',
    severity: queryParams.get('severity') || '',
    days: queryParams.get('days') || 'all',
    page: 1,
    limit: 20
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  });

  const statusConfig = {
    pending: { 
      color: 'text-yellow-400', 
      bg: 'bg-yellow-900/30', 
      border: 'border-yellow-800/50', 
      icon: <FiClock />, 
      label: 'Pending Review' 
    },
    reviewed: { 
      color: 'text-blue-400', 
      bg: 'bg-blue-900/30', 
      border: 'border-blue-800/50', 
      icon: <FiCheckCircle />, 
      label: 'Reviewed' 
    },
    dismissed: { 
      color: 'text-gray-400', 
      bg: 'bg-gray-700', 
      border: 'border-gray-600', 
      icon: <FiXCircle />, 
      label: 'Dismissed' 
    }
  };

  const severityConfig = {
    low: { 
      color: 'text-green-400', 
      bg: 'bg-green-900/30', 
      border: 'border-green-800/50', 
      icon: <FiAlertTriangle />, 
      label: 'Low' 
    },
    medium: { 
      color: 'text-yellow-400', 
      bg: 'bg-yellow-900/30', 
      border: 'border-yellow-800/50', 
      icon: <FiAlertTriangle />, 
      label: 'Medium' 
    },
    high: { 
      color: 'text-red-400', 
      bg: 'bg-red-900/30', 
      border: 'border-red-800/50', 
      icon: <FiAlertTriangle />, 
      label: 'High' 
    }
  };

  const actionOptions = [
    { 
      value: 'dismiss', 
      label: 'Dismiss Report', 
      icon: <FiCheckCircle className="w-4 h-4" />, 
      color: 'text-green-400',
      bg: 'bg-green-900/30',
      description: 'Mark this report as false or invalid'
    },
    { 
      value: 'warn_seller', 
      label: 'Warn Seller', 
      icon: <FiAlertTriangle className="w-4 h-4" />, 
      color: 'text-yellow-400',
      bg: 'bg-yellow-900/30',
      description: 'Send warning to seller about the listing'
    },
    { 
      value: 'suspend_listing', 
      label: 'Suspend Listing', 
      icon: <FiShield className="w-4 h-4" />, 
      color: 'text-orange-400',
      bg: 'bg-orange-900/30',
      description: 'Temporarily suspend this listing'
    },
    { 
      value: 'delete_listing', 
      label: 'Delete Listing', 
      icon: <FiTrash2 className="w-4 h-4" />, 
      color: 'text-red-400',
      bg: 'bg-red-900/30',
      description: 'Permanently remove this listing'
    }
  ];

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      // Clean up params
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      if (params.days === 'all') delete params.days;
      if (!params.severity) delete params.severity;
      
      console.log('Fetching LISTINGS with params:', params);
      
      // FIX: Call getReportedListings, not getReportedSellers
      const response = await getReportedListings(params);
      
      console.log('API Response:', response);
      
      // Handle the response structure - assuming response.data has the data
      const apiData = response.data?.data || response.data || response;
      
      console.log('API Data to use:', apiData);
      
      // Set state
      setReports(apiData.reports || []);
      setStats(apiData.statistics || []);
      setPagination(apiData.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
      });
      
    } catch (error) {
      console.error('Error:', error);
      alert(error.response?.data?.message || 'Failed to load reports.');
      // Reset on error
      setReports([]);
      setStats([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [filters.page, filters.status, filters.severity, filters.days, searchTerm]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value, page: 1 };
    setFilters(newFilters);
    
    const searchParams = new URLSearchParams();
    if (newFilters.status) searchParams.set('status', newFilters.status);
    if (newFilters.severity) searchParams.set('severity', newFilters.severity);
    if (newFilters.days && newFilters.days !== 'all') searchParams.set('days', newFilters.days);
    
    navigate({ search: searchParams.toString() });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchReports();
  };

  const handleAction = async (reportId, action, adminNotes = '', severity = 'medium', sendEmail = true) => {
    try {
      setActionLoading(reportId);
      setError('');
      
      const report = reports.find(r => r.report_id === reportId);
      if (!report) {
        throw new Error('Report not found');
      }
      
      const sellerUserId = report.seller_user_id;
      if (!sellerUserId) {
        throw new Error('Seller information not found');
      }
      
      await handleListingReport(
        reportId, 
        action, 
        adminNotes, 
        severity, 
        sendEmail,
        sellerUserId
      );
      
      setSuccessMessage('Action completed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchReports();
      setSelectedReports(prev => prev.filter(id => id !== reportId));
      setActionModalData(null);
    } catch (error) {
      setError('Failed to complete action: ' + (error.response?.data?.message || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedReports.length === 0) {
      setError('Please select reports and an action.');
      return;
    }

    const confirmAction = window.confirm(
      `Are you sure you want to ${bulkAction.replace('_', ' ')} ${selectedReports.length} report(s)?`
    );
    
    if (!confirmAction) return;

    try {
      setError('');
      await bulkHandleListingReports(selectedReports, bulkAction);
      setSuccessMessage(`Successfully processed ${selectedReports.length} report(s)!`);
      setTimeout(() => setSuccessMessage(''), 3000);
      setSelectedReports([]);
      setBulkAction('');
      fetchReports();
    } catch (error) {
      setError('Failed to process bulk action: ' + error.message);
    }
  };

  const toggleSelectReport = (reportId) => {
    setSelectedReports(prev =>
      prev.includes(reportId)
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedReports.length === reports.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(reports.map(r => r.report_id));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₦0.00';
    return `₦${parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
  };

  const getDaysAgo = (dateString) => {
    if (!dateString) return 0;
    const reportedDate = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - reportedDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const exportReports = () => {
    try {
      const data = {
        exported_at: new Date().toISOString(),
        total_reports: reports.length,
        filters: filters,
        reports: reports.map(r => ({
          report_id: r.report_id,
          listing_title: r.listing_title,
          seller: r.store_name,
          reporter: `${r.reporter_first_name} ${r.reporter_last_name}`,
          reason: r.reason,
          status: r.status,
          severity: r.severity,
          reported_date: r.reported_date_formatted,
          days_pending: r.days_pending
        }))
      };
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reported-listings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setSuccessMessage('Reports exported successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setError('Failed to export reports: ' + error.message);
    }
  };

  // Loading Skeleton
  if (loading && reports.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="h-4 bg-gray-700 rounded w-24 mb-3"></div>
                <div className="h-8 bg-gray-700 rounded w-16"></div>
              </div>
            ))}
          </div>
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-700 rounded w-full"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const pendingCount = stats.find(s => s.status === 'pending')?.count || 0;
  const reviewedCount = stats.find(s => s.status === 'reviewed')?.count || 0;
  const dismissedCount = stats.find(s => s.status === 'dismissed')?.count || 0;
  const totalReports = pagination.total || 0;

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6">
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertCircle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Reported</span> Listings
            </h1>
            <p className="text-gray-400 mt-1">Review and take action on listings reported by users</p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => fetchReports()}
              disabled={loading}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-blue-500 transition-all duration-200 font-medium flex items-center disabled:opacity-50"
            >
              <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <button
              onClick={exportReports}
              className="px-4 py-2.5 bg-gray-800 text-white border border-gray-700 rounded-lg hover:bg-gray-700 hover:border-green-500 transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
            >
              <FiDownload className="mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-yellow-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Pending Review</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-yellow-300 transition-colors">
                {pendingCount}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Awaiting moderation
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-yellow-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiClock className="text-yellow-400 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-blue-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Reviewed</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-blue-300 transition-colors">
                {reviewedCount}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Processed reports
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiCheckCircle className="text-blue-400 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Dismissed</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-gray-300 transition-colors">
                {dismissedCount}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                False reports
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiXCircle className="text-gray-400 text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-green-500/50 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Reports</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-green-300 transition-colors">
                {totalReports}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                All time
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiBarChart2 className="text-green-400 text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiFilter className="text-[#ff660e]" />
              <h3 className="font-medium text-white">Filters & Search</h3>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-[#ff660e] hover:text-orange-400 font-medium flex items-center transition-colors"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              {showFilters ? <FiChevronRight className="ml-1 transform rotate-90" /> : <FiChevronRight className="ml-1" />}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="pending">Pending Review</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="dismissed">Dismissed</option>
                  <option value="">All Status</option>
                </select>
              </div>

              {/* Severity Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Severity
                </label>
                <select
                  value={filters.severity}
                  onChange={(e) => handleFilterChange('severity', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="">All Severity</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              {/* Days Pending Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Days Pending
                </label>
                <select
                  value={filters.days}
                  onChange={(e) => handleFilterChange('days', e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white"
                >
                  <option value="all">All Days</option>
                  <option value="1">1+ days</option>
                  <option value="3">3+ days</option>
                  <option value="7">7+ days</option>
                  <option value="30">30+ days</option>
                </select>
              </div>

              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Search
                </label>
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative flex-1">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search listings, sellers..."
                      className="w-full pl-10 pr-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-4 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium hover:scale-[1.02]"
                  >
                    Search
                  </button>
                </form>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
              <button
                onClick={() => {
                  setFilters({
                    status: 'pending',
                    severity: '',
                    days: 'all',
                    page: 1,
                    limit: 20
                  });
                  setSearchTerm('');
                  setShowFilters(false);
                }}
                className="px-4 py-2 text-gray-400 hover:text-gray-300 text-sm font-medium hover:bg-gray-700 rounded-lg transition-colors"
              >
                Clear All Filters
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowFilters(false)}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium hover:scale-[1.02]"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowFilters(false);
                    fetchReports();
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center hover:scale-[1.02]"
                >
                  <FiFilter className="mr-2" />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedReports.length > 0 && (
        <div className="mb-6 bg-gray-800 rounded-xl border border-gray-700 p-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-[#ff660e]/20 flex items-center justify-center">
                <FiPackage className="text-[#ff660e]" />
              </div>
              <div>
                <h3 className="font-medium text-white">Bulk Actions</h3>
                <p className="text-sm text-gray-400">
                  {selectedReports.length} report{selectedReports.length === 1 ? '' : 's'} selected
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-[#ff660e] focus:border-transparent"
              >
                <option value="">Choose Action</option>
                {actionOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                onClick={handleBulkAction}
                disabled={!bulkAction}
                className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
              <button
                onClick={() => setSelectedReports([])}
                className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 hover:text-white transition-colors font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-400">
          {loading ? 'Loading...' : `Showing ${reports.length} reports`}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'table'
                ? 'bg-[#ff660e] text-white'
                : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            <FiList />
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'card'
                ? 'bg-[#ff660e] text-white'
                : 'bg-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            <FiGrid />
          </button>
        </div>
      </div>

      {/* Reports Content */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {viewMode === 'table' ? (
          /* Table View */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-16">
                    <input
                      type="checkbox"
                      checked={reports.length > 0 && selectedReports.length === reports.length}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Report Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Listing & Seller
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status & Severity
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {reports.length > 0 ? (
                  reports.map((report) => {
                    const status = statusConfig[report.status] || statusConfig.pending;
                    const severity = severityConfig[report.severity] || severityConfig.medium;
                    
                    return (
                      <tr key={report.report_id} className="hover:bg-gray-750 transition-colors">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedReports.includes(report.report_id)}
                            onChange={() => toggleSelectReport(report.report_id)}
                            className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <h4 className="font-medium text-white">
                              Report #{report.report_id}
                            </h4>
                            <p className="text-sm text-gray-400 mb-2">
                              <strong>Reason:</strong> {report.reason}
                            </p>
                            {report.description && (
                              <p className="text-sm text-gray-500 line-clamp-2">
                                {report.description}
                              </p>
                            )}
                            <div className="flex items-center space-x-2 mt-2">
                              <div className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center">
                                <FiUser className="w-3 h-3 text-gray-400" />
                              </div>
                              <span className="text-sm text-gray-400">
                                {report.reporter_first_name} {report.reporter_last_name}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            <div>
                              <h5 className="font-medium text-white">
                                {report.listing_title}
                              </h5>
                              <p className="text-sm text-green-400">
                                {formatCurrency(report.price)}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center">
                                <FiPackage className="w-3 h-3 text-gray-400" />
                              </div>
                              <span className="text-sm text-gray-400">
                                {report.store_name}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              Views: {report.view_count || 0} • Reports: {report.report_count || 0}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color} ${status.border}`}>
                              {status.icon}
                              <span className="ml-1">{status.label}</span>
                            </span>
                            {report.severity && (
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${severity.bg} ${severity.color} ${severity.border}`}>
                                {severity.icon}
                                <span className="ml-1">{severity.label}</span>
                              </span>
                            )}
                            {report.days_pending > 0 && (
                              <div className="text-xs text-gray-500">
                                Pending for {report.days_pending} day(s)
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-400">
                            {formatDate(report.created_at)}
                          </div>
                          {report.reviewed_at && (
                            <div className="text-xs text-gray-500">
                              Reviewed: {formatDate(report.reviewed_at)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col space-y-2">
                            <button
                              onClick={() => navigate(`/admin/reports/listings/${report.report_id}`)}
                              className="px-3 py-1.5 bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                            >
                              <FiEye className="mr-1" />
                              View Details
                            </button>
                            
                            {report.status === 'pending' && (
                              <div className="relative group">
                                <button className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center justify-center w-full">
                                  <FiMoreVertical className="mr-1" />
                                  Take Action
                                </button>
                                <div className="absolute right-0 mt-1 w-56 bg-gray-800 rounded-xl border border-gray-700 shadow-xl z-10 hidden group-hover:block">
                                  <div className="p-2">
                                    {actionOptions.map((option) => (
                                      <button
                                        key={option.value}
                                        onClick={() => {
                                          setActionModalData({
                                            reportId: report.report_id,
                                            action: option.value,
                                            report: report
                                          });
                                          setShowActionModal(true);
                                        }}
                                        className={`flex items-center gap-3 w-full px-3 py-2.5 text-left hover:bg-gray-700 rounded-lg transition-colors ${option.color}`}
                                      >
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${option.bg}`}>
                                          {option.icon}
                                        </div>
                                        <div>
                                          <div className="font-medium">{option.label}</div>
                                          <div className="text-xs text-gray-400">{option.description}</div>
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="text-gray-600 text-6xl mb-6">✅</div>
                      <h3 className="text-xl font-medium text-white mb-2">No reports found</h3>
                      <p className="text-gray-400 mb-6">
                        {filters.status === 'pending' 
                          ? 'No pending reports to review. Great job!'
                          : 'No reports match your current filters.'}
                      </p>
                      <button
                        onClick={() => {
                          setFilters({
                            status: 'pending',
                            severity: '',
                            days: 'all',
                            page: 1,
                            limit: 20
                          });
                          setSearchTerm('');
                        }}
                        className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
                      >
                        Clear Filters
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* Card View */
          <div className="p-6">
            {reports.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reports.map((report) => {
                  const status = statusConfig[report.status] || statusConfig.pending;
                  const severity = severityConfig[report.severity] || severityConfig.medium;
                  
                  return (
                    <div key={report.report_id} className="bg-gray-700/30 rounded-xl border border-gray-600 p-4 hover:border-gray-500 transition-colors group">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedReports.includes(report.report_id)}
                            onChange={() => toggleSelectReport(report.report_id)}
                            className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                          />
                          <div className="w-10 h-10 rounded-lg bg-gray-700 flex items-center justify-center">
                            <FiAlertTriangle className="text-gray-400" />
                          </div>
                        </div>
                        <div className="flex flex-col items-end space-y-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                            {status.icon}
                            <span className="ml-1">{status.label}</span>
                          </span>
                          {report.severity && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${severity.bg} ${severity.color}`}>
                              {severity.icon}
                              <span className="ml-1">{severity.label}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <h4 className="font-medium text-white mb-2 group-hover:text-[#ff660e] transition-colors">
                        #{report.report_id} - {report.listing_title}
                      </h4>
                      
                      <div className="mb-3">
                        <p className="text-sm text-gray-400">
                          <strong>Reason:</strong> {report.reason}
                        </p>
                        {report.description && (
                          <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                            {report.description}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center">
                            <FiPackage className="w-3 h-3 text-gray-400" />
                          </div>
                          <span className="text-sm text-gray-300 truncate">
                            {report.store_name}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDate(report.created_at)}
                        </span>
                      </div>
                      
                      <div className="flex space-x-2 pt-4 border-t border-gray-600">
                        <button
                          onClick={() => navigate(`/admin/reports/listings/${report.report_id}`)}
                          className="flex-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                        >
                          <FiEye className="mr-1" />
                          View
                        </button>
                        {report.status === 'pending' && (
                          <button
                            onClick={() => {
                              setActionModalData({
                                reportId: report.report_id,
                                action: 'dismiss',
                                report: report
                              });
                              setShowActionModal(true);
                            }}
                            className="flex-1 px-3 py-1.5 bg-[#ff660e]/20 hover:bg-[#ff660e]/30 text-[#ff660e] rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                          >
                            <FiMoreVertical className="mr-1" />
                            Action
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-600 text-6xl mb-6">✅</div>
                <h3 className="text-xl font-medium text-white mb-2">No reports found</h3>
                <p className="text-gray-400 mb-6">
                  {filters.status === 'pending' 
                    ? 'No pending reports to review. Great job!'
                    : 'No reports match your current filters.'}
                </p>
                <button
                  onClick={() => {
                    setFilters({
                      status: 'pending',
                      severity: '',
                      days: 'all',
                      page: 1,
                      limit: 20
                    });
                    setSearchTerm('');
                  }}
                  className="px-6 py-2.5 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium inline-flex items-center hover:scale-[1.02]"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        {reports.length > 0 && pagination && pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-sm text-gray-400">
              Showing {((pagination.page || 1) - 1) * (pagination.limit || 20) + 1} to {Math.min((pagination.page || 1) * (pagination.limit || 20), pagination.total || 0)} of {pagination.total || 0} reports
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleFilterChange('page', (pagination.page || 1) - 1)}
                disabled={(pagination.page || 1) <= 1}
                className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronLeft className="text-gray-300" />
              </button>
              
              <div className="flex items-center space-x-1">
                {[...Array(Math.min(5, pagination.pages || 1)).keys()].map(i => {
                  const pageNum = i + 1;
                  const isCurrent = pageNum === (pagination.page || 1);
                  
                  let showPage = false;
                  if ((pagination.pages || 1) <= 5) {
                    showPage = true;
                  } else if (pageNum <= 2 || pageNum >= (pagination.pages || 1) - 1 || Math.abs(pageNum - (pagination.page || 1)) <= 1) {
                    showPage = true;
                  }
                  
                  if (showPage) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handleFilterChange('page', pageNum)}
                        className={`w-8 h-8 rounded-lg transition-colors ${
                          isCurrent 
                            ? 'bg-[#ff660e] text-white' 
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === 3 && (pagination.pages || 1) > 5) {
                    return <span key="ellipsis" className="px-2 text-gray-500">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <button
                onClick={() => handleFilterChange('page', (pagination.page || 1) + 1)}
                disabled={(pagination.page || 1) >= (pagination.pages || 1)}
                className="p-2 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FiChevronRight className="text-gray-300" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {showActionModal && actionModalData && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md animate-fade-in">
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  Take Action on Report #{actionModalData.reportId}
                </h3>
                <button
                  onClick={() => {
                    setShowActionModal(false);
                    setActionModalData(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <FiX className="text-xl" />
                </button>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                {actionOptions.find(a => a.value === actionModalData.action)?.label} for listing:
                <strong className="text-white ml-1">{actionModalData.report.listing_title}</strong>
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Admin Notes *
                </label>
                <textarea
                  id="adminNotes"
                  rows="4"
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white placeholder-gray-500"
                  placeholder="Explain the reason for this action..."
                  defaultValue=""
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Severity
                </label>
                <select
                  id="severity"
                  className="w-full px-3 py-2.5 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white"
                >
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-gray-700/30 rounded-lg">
                <input
                  type="checkbox"
                  id="sendEmail"
                  defaultChecked
                  className="h-4 w-4 text-[#ff660e] focus:ring-[#ff660e] border-gray-600 rounded bg-gray-700"
                />
                <label htmlFor="sendEmail" className="text-sm text-gray-300">
                  Send email notification to involved parties
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  onClick={() => {
                    setShowActionModal(false);
                    setActionModalData(null);
                  }}
                  className="px-4 py-2 text-gray-300 bg-gray-700 border border-gray-600 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const adminNotes = document.getElementById('adminNotes').value;
                    const severity = document.getElementById('severity').value;
                    const sendEmail = document.getElementById('sendEmail').checked;
                    
                    if (!adminNotes.trim()) {
                      setError('Please provide admin notes.');
                      return;
                    }
                    
                    handleAction(
                      actionModalData.reportId,
                      actionModalData.action,
                      adminNotes,
                      severity,
                      sendEmail
                    );
                  }}
                  disabled={actionLoading === actionModalData.reportId}
                  className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === actionModalData.reportId ? 'Processing...' : 'Confirm Action'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

export default ReportedListings;